import React from "react";
import { API } from 'aws-amplify';
import forge from 'node-forge';
import * as Sentry from "@sentry/react";
import PageError from "../../components/PageError/PageError";
import { safeParseLocalStorage } from "../../utils/safeParseLocalStorage";

const publicKeyPem = process.env.REACT_APP_PUBLIC_KEY.replace(/\\n/g, '\n');

// Function to generate a random AES key and encrypt data
const encryptWithAES = (data) => {
    const aesKey = forge.random.getBytesSync(32); // 256-bit AES key
    const iv = forge.random.getBytesSync(16); // 128-bit IV

    const cipher = forge.cipher.createCipher('AES-CBC', aesKey);
    cipher.start({ iv: iv });
    cipher.update(forge.util.createBuffer(JSON.stringify(data), 'utf8'));
    cipher.finish();

    const encryptedData = cipher.output.getBytes();
    return {
        aesKey,
        iv,
        encryptedData: forge.util.encode64(encryptedData),
    };
};

// Function to encrypt AES key with RSA public key
const encryptAESKey = (publicKeyPem, aesKey, iv) => {
    const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
    const encryptedKey = publicKey.encrypt(aesKey + iv, 'RSA-OAEP', {
        md: forge.md.sha256.create(),
    });
    return forge.util.encode64(encryptedKey);
};

const safeStringify = (obj) => {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
        if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
                return "[Circular]";
            }
            seen.add(value);
        }
        return value;
    }, 2);
};
  

const sendErrorToOpsGenie = async (error, errorInfo, additionalContext) => {
    const payload = {
        message: `Root Web App Bladsy Fout - ${additionalContext.userContext?.boerderynaam || "Onbekende Boerdery"}`,
        description: `
        Error: ${error?.message || "N/A"}
        Context: ${safeStringify(additionalContext)}
        `,
        priority: "P3",
        tags: ["BS", "WebApp", "Error"],
        alias: additionalContext.url
    };

    const { aesKey, iv, encryptedData } = encryptWithAES(payload);
    const encryptedAESKey = encryptAESKey(publicKeyPem, aesKey, iv);
    const encryptedPayload = {
        encryptedData,
        encryptedAESKey
    };

    try {
        let apiName = "AWSApiGateway";
        let path = "/ops/alerts";
        await API.post(apiName, path, { body: encryptedPayload, headers: { "Content-Type": "application/json" }});
        console.log("Error reported to Ops");
    } catch (e) {
        console.error("Failed to send error to Ops:", e);
    }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state to trigger fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Collect additional context
    let userContext = null;
    try {
        userContext = safeParseLocalStorage("boerdery", null);
    } catch (_parseErr) {
        // Corrupt/absent localStorage must not block error reporting.
    }
    const additionalContext = {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV,
        // props: this.props,
        // state: this.state,
        userContext,
    };

    // Report to Sentry FIRST so render-phase crashes caught here are visible
    // in Sentry (stack traces, grouping, release tracking, replay) — not only
    // in OpsGenie. Runs before the OpsGenie POST so a failure there (or the
    // legacy unguarded userContext access below) can't swallow the Sentry event.
    Sentry.captureException(error, {
        contexts: {
            react: { componentStack: errorInfo?.componentStack },
            boerdery: userContext || undefined,
        },
        tags: {
            source: "ErrorBoundary",
            boerderyuuid: userContext?.boerderyuuid,
        },
    });

    sendErrorToOpsGenie(error, errorInfo, additionalContext);

    // Log error details (optional)
    console.error("Error caught by ErrorBoundary:", error, errorInfo);

    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Render the PageError component with error details
      return <PageError error={this.state.error} errorInfo={this.state.errorInfo} />;
    }

    // Render children components if no error
    return this.props.children;
  }
}

export default ErrorBoundary;